import { cn } from "@/lib/utils";
import { FilterButtonsProps } from "components/Explore/FilterButtons";
import { LiquidPillDropdown } from "components/ui/v2/liquid-pill-dropdown";
import { type CircleActionVariant } from "components/ui/v2/circle-action-variants";
import { useVendor } from "context/vendor/vendor.provider";
import {
  BookOpen,
  Cake,
  Calendar,
  Gift,
  MessageSquare,
  Settings2,
  SlidersHorizontal,
  Star,
  TvMinimalPlay,
  Users
} from "lucide-react";
import { createElement, useState } from "react";
import { EMessageType } from "types/Message.type";

interface ISelectExplore {
  isCompresed?: boolean;
  value: string;
  setValue: (value: string) => void;
}

type SelectExploreProps = FilterButtonsProps & ISelectExplore;

const messagesTypes = [
  { value: EMessageType.TEXT,            label: "Mensajes",     icon: MessageSquare },
  { value: EMessageType.CORPORATE_VALUE, label: "Valores",      icon: Star          },
  { value: EMessageType.MAGAZINE,        label: "Canva",        icon: BookOpen      },
  { value: EMessageType.CAPSULES,        label: "Cápsulas",     icon: TvMinimalPlay },
  { value: EMessageType.BIRTHDAY,        label: "Cumpleaños",   icon: Cake          },
  { value: EMessageType.ANNIVERSARY,     label: "Aniversarios", icon: Calendar      },
  { value: EMessageType.NEW_USERS,       label: "Ingresos",     icon: Users         },
  { value: EMessageType.BENEFIT,         label: "Beneficios",   icon: Gift          },
];

const TYPE_VARIANT: Record<string, CircleActionVariant> = {
  [EMessageType.ALL]:             "slate",
  [EMessageType.TEXT]:            "indigo",
  [EMessageType.CORPORATE_VALUE]: "gold",
  [EMessageType.MAGAZINE]:        "sky",
  [EMessageType.CAPSULES]:        "violet",
  [EMessageType.BIRTHDAY]:        "rose",
  [EMessageType.ANNIVERSARY]:     "sunset",
  [EMessageType.NEW_USERS]:       "emerald",
  [EMessageType.CERTIFICATE]:     "emerald",
  [EMessageType.BENEFIT]:         "violet",
};

export default function SelectExplore(props: SelectExploreProps) {
  const [hoveredType, setHoveredType] = useState<string | null>(null);
  const { vendor } = useVendor();
  const { value, setValue } = props;

  if (
    vendor?.enableCertificates &&
    !messagesTypes.some((t) => t.value.toUpperCase() === EMessageType.CERTIFICATE)
  ) {
    messagesTypes.push({ value: EMessageType.CERTIFICATE, label: "Certificados", icon: Users });
  }

  const availableMessageTypes = [
    { value: EMessageType.ALL, label: "Todas", icon: SlidersHorizontal },
    ...messagesTypes.filter((type) => {
      if (type.value.toUpperCase() === EMessageType.CORPORATE_VALUE && !vendor?.enableCorporateValue) return false;
      if (type.value.toUpperCase() === EMessageType.BIRTHDAY        && !vendor?.enableBirthdayAndAnniversary) return false;
      if (type.value.toUpperCase() === EMessageType.ANNIVERSARY     && !vendor?.enableBirthdayAndAnniversary) return false;
      if (type.value.toUpperCase() === EMessageType.NEW_USERS       && !vendor?.enableAdSync) return false;
      return true;
    }),
  ];

  const normalizedValue =
    !value || value.toUpperCase() === EMessageType.ALL.toUpperCase()
      ? EMessageType.ALL
      : value;

  const handleSelect = (selectedValue: string) => {
    const isAll = selectedValue.toUpperCase() === EMessageType.ALL.toUpperCase();
    const isSameSelection =
      selectedValue.toUpperCase() === normalizedValue.toUpperCase();
    const newValue = isSameSelection && !isAll ? "" : selectedValue;
    setValue(newValue === EMessageType.ALL ? "" : newValue);

    switch (newValue.toUpperCase()) {
      case EMessageType.ALL:           props.filteredMessages(EMessageType.ALL);             break;
      case EMessageType.TEXT:            props.filteredMessages(EMessageType.TEXT);            break;
      case EMessageType.CORPORATE_VALUE: props.filteredMessages(EMessageType.CORPORATE_VALUE); break;
      case EMessageType.MAGAZINE:        props.filteredMessages(EMessageType.MAGAZINE);        break;
      case EMessageType.CAPSULES:        props.filteredMessages(EMessageType.CAPSULES);        break;
      case EMessageType.BIRTHDAY:        props.filteredMessages(EMessageType.BIRTHDAY);        break;
      case EMessageType.ANNIVERSARY:     props.filteredMessages(EMessageType.ANNIVERSARY);     break;
      case EMessageType.NEW_USERS:       props.filteredMessages(EMessageType.NEW_USERS);       break;
      case EMessageType.BENEFIT:         props.filteredMessages(EMessageType.BENEFIT);         break;
      case EMessageType.CERTIFICATE:     props.filteredMessages(EMessageType.CERTIFICATE);     break;
      default:                           props.filteredMessages(EMessageType.ALL);             break;
    }
  };

  const activeType   = availableMessageTypes.find((t) => t.value.toUpperCase() === normalizedValue.toUpperCase());
  const hoveredEntry = hoveredType
    ? availableMessageTypes.find((t) => t.value.toUpperCase() === hoveredType.toUpperCase())
    : null;

  const displayIcon: typeof Settings2       = hoveredEntry?.icon ?? activeType?.icon ?? SlidersHorizontal;
  const displayVariant: CircleActionVariant = hoveredEntry
    ? (TYPE_VARIANT[hoveredEntry.value.toUpperCase()] ?? "slate")
    : (TYPE_VARIANT[normalizedValue.toUpperCase()] ?? "slate");

  const isFilterActive =
    hoveredType !== null ||
    normalizedValue.toUpperCase() !== EMessageType.ALL.toUpperCase();

  const dropdownItems = availableMessageTypes.map((type) => ({ 
    value: type.value,
    label: props.t(type.label),
    icon: type.icon,
    variant: TYPE_VARIANT[type.value.toUpperCase()] as CircleActionVariant | undefined,
  }));

  return (
    <LiquidPillDropdown
      items={dropdownItems}
      value={normalizedValue}
      onSelect={handleSelect}
      onHoverChange={setHoveredType}
      disabled={props.loadingMessages}
      active={isFilterActive}
      variant={displayVariant}
      icon={createElement(displayIcon, { size: 18, strokeWidth: 1.5 })}
      size="md"
      collapsedShape="circle"
      className={cn(props.loadingMessages && "animate-pulse opacity-70")}
      aria-label="Filtrar publicaciones"
    />
  );
}
